import { Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
    GetReferralDetails,
    WorklistItemDataSourceType,
    IWorklistItemDetailsDto,
    UrgencyType,
    DecisionType,
    UpsertNotification,
    EntityType,
    IWorklistItemDto,
    GetReferralPrior,
    GetLinkedOrder,
    ReferralStatus
} from '@app/api-module/control-centre-api-client';
import { ReferralsService } from '@app/referrals/referrals.service';
import { SnackbarService } from '@app/services/snackbar.sevice';
import { BehaviorSubject, catchError, combineLatest, distinctUntilChanged, filter, map, Observable, Subscription } from 'rxjs';
import { OrgSiteSelectionService } from '@app/shared/org-site-selection/org-site-selection.service';
import { CommonUtilities } from '@app/shared/common-utilities';
import { WorklistService } from '@app/worklist/worklist.service';
import { OrderDetailsRow } from './imaging-request-orders-details/imaging-request-orders-details.component';
import { DateTime } from 'luxon';
import { OrderStatusClassMapping } from '../models/order-status-class-mappings';
import { v4 as uuidv4 } from 'uuid';
import { HubConnection } from '@microsoft/signalr';
import { HeaderBadge } from '../models/header-badge.model';
import { ClassToDisplayMapping } from '../models/class-display-mapping';
import {
    ImagingRequest,
    isOrderScanRegion,
    isReferralScanRegion,
    OrderScanRegion,
    ReferralScanRegion,
    ScanRegion
} from '../models/imaging-requests.model';
import { ImagingRequestStatus, ImagingRequestType } from '../models/enums';
import { ScanRegionStatusClassMapping } from '../models/scan-region-status-class-mappings';
import { ContrastAllergyDropdownMapping, DefaultDropdownValue, PregnancyDropdownMapping } from '../imaging-request-form/constants';

@Component({
    selector: 'app-imaging-request-details-modal',
    templateUrl: './imaging-request-details-modal.component.html',
    styleUrl: './imaging-request-details-modal.component.scss'
})
export class ImagingRequestDetailsModalComponent implements OnInit, OnDestroy {
    CommonUtilities = CommonUtilities;
    ScanRegionStatusClassMapping = ScanRegionStatusClassMapping;
    ClassToDisplayMapping = ClassToDisplayMapping;
    headerBadges: HeaderBadge[];
    imagingRequest: ImagingRequest;
    orderScanRegions$: BehaviorSubject<OrderScanRegion[]> = new BehaviorSubject<OrderScanRegion[]>([]);

    imagingRequest$: BehaviorSubject<ImagingRequest | null> = new BehaviorSubject<ImagingRequest | null>(null);
    referralDetails$: BehaviorSubject<GetReferralDetails | null> = new BehaviorSubject<GetReferralDetails | null>(null);
    ordersDetails$: BehaviorSubject<IWorklistItemDetailsDto[]> = new BehaviorSubject<IWorklistItemDetailsDto[]>([]);
    orderRows$: Observable<OrderDetailsRow[]>;
    onlyOneTab: boolean = true;
    showScanRegionDecisionColumns$: Observable<boolean>;

    pregnancyText$: BehaviorSubject<string> = new BehaviorSubject<string>('Pregnancy Unknown');
    pregnancyClass$: BehaviorSubject<string> = new BehaviorSubject<string>('');
    contrastAllergyText$: BehaviorSubject<string> = new BehaviorSubject<string>('Unknown Contrast Allergy');
    contrastAllergyClass$: BehaviorSubject<string> = new BehaviorSubject<string>('');
    differentialDiagnosis$: BehaviorSubject<string> = new BehaviorSubject<string>('');
    egfrKnown$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    paediatricConsultation$: BehaviorSubject<string> = new BehaviorSubject<string>('N/A');
    callRequested$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    latestDecisionReason$: BehaviorSubject<string> = new BehaviorSubject<string>('');

    showClinicalDiscussionToggle: boolean = false;

    clinicalHistories: string[];
    egfrResult: string;
    approvedBy: string;
    authorityToRequestScans: boolean;
    isTrauma: boolean;
    isStroke: boolean;
    priors: Prior[];
    referralScanRequests: ScanRegion[];

    private readonly _deletedReferralItemIds$: BehaviorSubject<Set<number>> = new BehaviorSubject<Set<number>>(new Set<number>());
    private readonly _deletedDecisionItemIds$: BehaviorSubject<Set<number>> = new BehaviorSubject<Set<number>>(new Set<number>());
    private readonly _deletedPriors$: BehaviorSubject<Set<number>> = new BehaviorSubject<Set<number>>(new Set<number>());
    private readonly _deletedLinkedOrderIds$: BehaviorSubject<Set<number>> = new BehaviorSubject<Set<number>>(new Set<number>());

    private readonly _pendingOrderUpdates$: BehaviorSubject<IWorklistItemDetailsDto[]> = new BehaviorSubject<IWorklistItemDetailsDto[]>([]);
    private readonly _pendingReferralUpdate$: BehaviorSubject<GetReferralDetails | null> = new BehaviorSubject<GetReferralDetails | null>(
        null
    );
    private readonly _pendingDeletedReferralItem$: BehaviorSubject<Set<number>> = new BehaviorSubject<Set<number>>(new Set<number>());
    private readonly _pendingDeletedDecisionItems$: BehaviorSubject<Set<number>> = new BehaviorSubject<Set<number>>(new Set<number>());
    private readonly _pendingDeletedPriors$: BehaviorSubject<Set<number>> = new BehaviorSubject<Set<number>>(new Set<number>());
    private readonly _pendingDeletedLinkedOrderIds$: BehaviorSubject<Set<number>> = new BehaviorSubject<Set<number>>(new Set<number>());
    hasPendingChanges$: Observable<boolean> = combineLatest([
        this._pendingOrderUpdates$,
        this._pendingReferralUpdate$,
        this._pendingDeletedReferralItem$,
        this._pendingDeletedDecisionItems$,
        this._pendingDeletedPriors$,
        this._pendingDeletedLinkedOrderIds$
    ]).pipe(
        map(
            ([
                pendingOrderUpdates,
                pendingReferralUpdate,
                pendingDeletedReferralItems,
                pendingDeletedDecisionItems,
                pendingDeletedPriors,
                pendingDeletedLinkedOrderIds
            ]) =>
                pendingOrderUpdates.length > 0 ||
                !!pendingReferralUpdate ||
                pendingDeletedReferralItems.size > 0 ||
                pendingDeletedDecisionItems.size > 0 ||
                pendingDeletedPriors.size > 0 ||
                pendingDeletedLinkedOrderIds.size > 0
        )
    );

    private readonly _subscriptions = new Subscription();

    private _hubSubscriptionId: string | null = null;
    private _hubConnection: HubConnection;

    userCanEditImagingRequests: boolean = false;

    constructor(
        private readonly snackbarService: SnackbarService,
        private readonly orgSiteSelectionService: OrgSiteSelectionService,
        private readonly referralsService: ReferralsService,
        private readonly worklistService: WorklistService,
        @Optional() public dialogRef: MatDialogRef<ImagingRequestDetailsModalComponent>,
        @Optional() @Inject(MAT_DIALOG_DATA) public data: { request: ImagingRequest; userCanEditImagingRequests: boolean }
    ) {
        if (data) {
            this.imagingRequest = data.request;
            this.userCanEditImagingRequests = data.userCanEditImagingRequests;
        } else {
            console.error('Imaging Request tile is missing data!');
            this.snackbarService.openSnackBar(
                'Unexpected data missing for this imaging request. Please contact the Everlight support team if this error recurs.',
                SnackbarService.SNACK_BAR_CLASS_ALERT
            );
            this.closeForm();
        }
    }

    async ngOnInit() {
        if (!this.imagingRequest) {
            return;
        }

        this.imagingRequest$.next(this.imagingRequest);
        this._subscriptions.add(
            this.imagingRequest$.subscribe(imagingRequest => {
                this.imagingRequest = imagingRequest;
                this.setupImagingRequestDetails(imagingRequest);
            })
        );
        this._subscriptions.add(
            combineLatest([this.imagingRequest$, this._deletedReferralItemIds$, this._deletedDecisionItemIds$]).subscribe(
                ([imagingRequest, deletedReferralItemIds, deletedDecisionItemIds]) => {
                    this.referralScanRequests = imagingRequest.scanRegions
                        .filter(scanRegion => isReferralScanRegion(scanRegion))
                        .filter((scanRegion: ReferralScanRegion) =>
                            this.isDecisionItem(scanRegion)
                                ? !deletedDecisionItemIds.has(scanRegion.id)
                                : !deletedReferralItemIds.has(scanRegion.id)
                        );
                }
            )
        );

        const orderRowsWithoutReport = this.orderScanRegions$.pipe(map(scanRegions => scanRegions.map(this.getOrderRowFromScanRegion)));
        this.orderRows$ = combineLatest([orderRowsWithoutReport, this.ordersDetails$]).pipe(
            map(([initialOrderRows, orderDetails]) =>
                initialOrderRows.map(orderRow => {
                    const matchingOrder = orderDetails.find(orderDetail => orderDetail.accessionNumber === orderRow.accessionNumber);
                    if (matchingOrder) {
                        return this.getOrderRowFromOrder(matchingOrder);
                    } else {
                        return orderRow;
                    }
                })
            )
        );

        if (this.imagingRequest.referralId) {
            this.showScanRegionDecisionColumns$ = this.referralDetails$.pipe(
                filter(referralDetail => !!referralDetail),
                map(
                    referralDetail =>
                        this.imagingRequest.status !== ImagingRequestStatus.ReferralInProgress &&
                        referralDetail.status !== ReferralStatus.OnHold
                ),
                distinctUntilChanged()
            );
            this._subscriptions.add(
                this.referralsService.getReferral(this.imagingRequest.referralId).subscribe(details => {
                    this.referralDetails$.next(details);
                })
            );
        }
        this.referralDetails$.pipe(filter(referralDetails => !!referralDetails)).subscribe(referralDetails => {
            this.setupReferralDetails(referralDetails);
        });

        const accessionNumbers =
            this.imagingRequest.scanRegions
                ?.filter(scanRegion => isOrderScanRegion(scanRegion))
                .map((scanRegion: OrderScanRegion) => scanRegion.accessionNumber) ?? [];

        this.populateOrdersDetails(accessionNumbers);

        this._subscriptions.add(
            combineLatest([this.referralDetails$, this.ordersDetails$, this._deletedPriors$]).subscribe(
                ([referralDetails, ordersDetails, deletedPriors]) => {
                    const referralPriors = referralDetails?.priors?.filter(prior => !deletedPriors.has(prior.id)) ?? [];
                    this.priors = this.getPriors(referralPriors, ordersDetails);
                }
            )
        );

        // Initialise SignalR updates
        await this.initialiseSignalRUpdates();
    }

    mergePendingChanges(): void {
        // Merge referral & order changes in
        const referralDetails = this._pendingReferralUpdate$.value;
        const orders = this._pendingOrderUpdates$.value;
        const deletedLinkedOrderIds = this._pendingDeletedLinkedOrderIds$.value;

        if (referralDetails || orders.length > 0 || deletedLinkedOrderIds.size > 0) {
            const mergeResult = this.imagingRequest.mergeWithChanges(referralDetails, orders, deletedLinkedOrderIds);
            const mergeIsDifferent =
                mergeResult.referralChanged || mergeResult.updatedOrderAccessions.length > 0 || mergeResult.removedOrderAccessions.size > 0;
            if (mergeIsDifferent) {
                if (this.userCanEditImagingRequests && mergeResult.imagingRequest.editableRequest) {
                    // this is now an editable request
                    // close this dialog and tell the parent to open an editable equivalent
                    this.dialogRef.close({ reopen: true, data: mergeResult.imagingRequest });

                    // if we don't return the rest of this executes
                    return;
                }

                this.imagingRequest$.next(mergeResult.imagingRequest);

                if (mergeResult.referralChanged) {
                    this.referralDetails$.next(referralDetails);
                }

                if (mergeResult.updatedOrderAccessions.length > 0) {
                    const newOrders = [...this.ordersDetails$.value];
                    mergeResult.updatedOrderAccessions.forEach(updatedOrderAccession => {
                        const indexOfInitialOrder = newOrders.findIndex(order => order.accessionNumber === updatedOrderAccession);
                        if (indexOfInitialOrder !== -1) {
                            newOrders.splice(indexOfInitialOrder, 1);
                        }
                    });

                    this.ordersDetails$.next(newOrders);
                    this.populateOrdersDetails(mergeResult.updatedOrderAccessions);
                }

                this.setupImagingRequestDetails(mergeResult.imagingRequest);
            }
        }

        this._pendingReferralUpdate$.next(null);
        this._pendingOrderUpdates$.next([]);

        // Set deleted referral/decision items
        this.mergePendingDeletedScanRegions(this._pendingDeletedReferralItem$, this._deletedReferralItemIds$);
        this.mergePendingDeletedScanRegions(this._pendingDeletedDecisionItems$, this._deletedDecisionItemIds$);
        this.mergePendingDeletedLinkedOrders(this._pendingDeletedLinkedOrderIds$, this._deletedLinkedOrderIds$);

        // Set deleted priors
        const pendingDeletedPriors = this._pendingDeletedPriors$.value;
        if (pendingDeletedPriors.size > 0) {
            const deletedPriors = this._deletedPriors$.value;
            const mergedDeletedPriors = new Set<number>([...deletedPriors, ...pendingDeletedPriors]);
            this._deletedPriors$.next(mergedDeletedPriors);
            this._pendingDeletedPriors$.next(new Set<number>());
        }
    }

    private mergePendingDeletedScanRegions(
        pendingDeletedScanRegionIds$: BehaviorSubject<Set<number>>,
        deletedScanRegionIds$: BehaviorSubject<Set<number>>
    ) {
        const pendingDeletedScanRegionIds = pendingDeletedScanRegionIds$.value;
        const deletedScanRegionIds = deletedScanRegionIds$.value;
        if (pendingDeletedScanRegionIds.size > 0) {
            const mergedDeletedScanRegionIds = new Set<number>([...deletedScanRegionIds, ...pendingDeletedScanRegionIds]);
            deletedScanRegionIds$.next(mergedDeletedScanRegionIds);
            pendingDeletedScanRegionIds$.next(new Set<number>());
        }
    }

    private mergePendingDeletedLinkedOrders(
        pendingDeletedLinkedOrders$: BehaviorSubject<Set<number>>,
        deletedLinkedOrders$: BehaviorSubject<Set<number>>
    ): Set<number> {
        const pendingDeletedLinkedOrders = pendingDeletedLinkedOrders$.value;
        const deletedLinkedOrders = deletedLinkedOrders$.value;
        if (pendingDeletedLinkedOrders.size > 0) {
            const mergedUnlinkedOrders = new Set<number>([...deletedLinkedOrders, ...pendingDeletedLinkedOrders]);
            deletedLinkedOrders$.next(mergedUnlinkedOrders);
            pendingDeletedLinkedOrders$.next(new Set<number>());
            return mergedUnlinkedOrders;
        }
        return deletedLinkedOrders;
    }

    private setupImagingRequestDetails(imagingRequest: ImagingRequest) {
        this.setupStrokeTrauma(imagingRequest);
        const orderScanRegions: OrderScanRegion[] = imagingRequest.scanRegions
            .filter(scanRegion => isOrderScanRegion(scanRegion))
            .map((scanRegion: OrderScanRegion) => scanRegion);
        this.onlyOneTab = orderScanRegions.length === 0 || orderScanRegions.length === imagingRequest.scanRegions.length;
        this.orderScanRegions$.next(orderScanRegions);
        this.headerBadges =
            imagingRequest.headerBadgeClasses?.map(badgeClass => ({
                class: badgeClass,
                display: ClassToDisplayMapping.get(badgeClass)
            })) ?? [];
        this.showClinicalDiscussionToggle =
            (imagingRequest.type === ImagingRequestType.ReferralWithOrders ||
                imagingRequest.type === ImagingRequestType.ReferralWithoutOrders) &&
            (imagingRequest.status === ImagingRequestStatus.ReferralInProgress ||
                imagingRequest.status === ImagingRequestStatus.ActionNeeded);
    }

    private setupStrokeTrauma(imagingRequest: ImagingRequest): void {
        switch (imagingRequest.urgency) {
            case UrgencyType.TraumaAndStroke:
                this.isTrauma = true;
                this.isStroke = true;
                break;
            case UrgencyType.Stroke:
                this.isTrauma = false;
                this.isStroke = true;
                break;
            case UrgencyType.Trauma:
                this.isTrauma = true;
                this.isStroke =
                    imagingRequest.isSimpleStroke ||
                    imagingRequest.isThrombectomy ||
                    imagingRequest.isThrombolysis ||
                    imagingRequest.isUnspecifiedStroke;
                break;
            default:
                this.isTrauma = false;
                this.isStroke =
                    imagingRequest.isSimpleStroke ||
                    imagingRequest.isThrombectomy ||
                    imagingRequest.isThrombolysis ||
                    imagingRequest.isUnspecifiedStroke;
                break;
        }
    }

    private setupReferralDetails(referralDetails: GetReferralDetails) {
        const sortedReferralDecisions = referralDetails.decisions
            ?.filter(decision => decision.decisionType !== DecisionType.Pending)
            ?.sort((decisionA, decisionB) => (DateTime.fromISO(decisionA.decidedAt) < DateTime.fromISO(decisionB.decidedAt) ? 1 : -1));
        const latestReferralDecision = sortedReferralDecisions?.length > 0 ? sortedReferralDecisions[0] : null;
        this.authorityToRequestScans = referralDetails.confirmation;
        this.approvedBy = referralDetails.approvedByDoctor;
        const uniqueClinicalNotes = new Set<string>(sortedReferralDecisions?.map(decision => decision.clinicalNotes) ?? []);
        this.clinicalHistories = referralDetails.clinicalHistory
            ? [...Array.from(uniqueClinicalNotes), referralDetails.clinicalHistory]
            : Array.from(uniqueClinicalNotes);
        const pregnancyMapping = PregnancyDropdownMapping.get(referralDetails.pregnancy ?? DefaultDropdownValue);
        this.pregnancyText$.next(pregnancyMapping.display);
        this.pregnancyClass$.next(pregnancyMapping.backgroundColor);
        const contrastAllergyMapping = ContrastAllergyDropdownMapping.get(referralDetails.contrastAllergy ?? DefaultDropdownValue);
        this.contrastAllergyText$.next(contrastAllergyMapping.display);
        this.contrastAllergyClass$.next(contrastAllergyMapping.backgroundColor);
        this.differentialDiagnosis$.next(referralDetails.differentialDiagnosis ?? '');
        this.egfrResult = latestReferralDecision?.egfrResult ?? referralDetails.egfrResult;
        const egfrKnown = !!this.egfrResult && this.egfrResult !== 'Unknown';
        this.egfrKnown$.next(egfrKnown);
        let pediatricConsultationText: string;
        switch (referralDetails.hasPriorPaediatricConsultation) {
            case true:
                pediatricConsultationText = 'Yes';
                break;
            case false:
                pediatricConsultationText = 'No';
                break;
            default:
                pediatricConsultationText = 'N/A';
                break;
        }
        this.paediatricConsultation$.next(pediatricConsultationText);
        const discussionStatuses = referralDetails.clinicalDiscussions?.map(discussion => discussion.status as unknown as string);
        const hasOpenClinicalDiscussions = discussionStatuses?.includes('Open') ?? false;
        this.callRequested$.next(hasOpenClinicalDiscussions);
        this.latestDecisionReason$.next(latestReferralDecision?.reason ?? '');
    }

    private populateOrdersDetails(accessionNumbers: string[]): void {
        accessionNumbers.forEach(accessionNumber => {
            this._subscriptions.add(
                this.worklistService
                    .get(accessionNumber, WorklistItemDataSourceType.Order)
                    .pipe(catchError(error => CommonUtilities.getErrorMessage(error)))
                    .subscribe({
                        next: orderDetail => this.handleGetOrderDetails(orderDetail),
                        error: error => this.snackbarService.openSnackBar(error, SnackbarService.SNACK_BAR_CLASS_ALERT)
                    })
            );
        });
    }

    private handleGetOrderDetails(orderDetail: IWorklistItemDetailsDto): void {
        this.ordersDetails$.next([...this.ordersDetails$.value, orderDetail]);
    }

    private getPriors(referralPriors: GetReferralPrior[], orders?: IWorklistItemDetailsDto[]): Prior[] {
        const orderPriors =
            orders?.flatMap(
                order =>
                    order.priors?.map(prior => ({
                        id: prior.priorId,
                        modality: prior.modality,
                        bodyPart: prior.studyDescription,
                        date: prior.studyDate
                    })) ?? []
            ) ?? [];
        const mappedReferralPriors =
            referralPriors.map(prior => ({
                id: prior.id,
                modality: prior.modality,
                bodyPart: prior.bodyPart,
                date: prior.priorDate
            })) ?? [];

        // Remove duplicate priors.
        orderPriors.forEach(orderPrior => {
            const matchingPriorIndex = mappedReferralPriors.findIndex(referralPrior => this.priorsAreEqual(referralPrior, orderPrior));
            if (matchingPriorIndex !== -1) {
                mappedReferralPriors.splice(matchingPriorIndex, 1);
            }
        });

        return [...orderPriors, ...mappedReferralPriors];
    }

    private priorsAreEqual(referralPrior: Prior, orderPrior: Prior): boolean {
        // Referral prior's format is different to order priors, so unify them.
        return (
            referralPrior.modality === orderPrior.modality &&
            referralPrior.bodyPart === orderPrior.bodyPart &&
            CommonUtilities.formatDateTime(referralPrior.date) === CommonUtilities.formatDateTime(orderPrior.date)
        );
    }

    private getOrderRowFromScanRegion(scanRegion: OrderScanRegion): OrderDetailsRow {
        return {
            accessionNumber: scanRegion.accessionNumber,
            modality: scanRegion.modality,
            studyDescription: scanRegion.bodyPart,
            statusDescriptionClass: ''
        };
    }

    private getOrderRowFromOrder(order: IWorklistItemDetailsDto): OrderDetailsRow {
        const statusClass = OrderStatusClassMapping.get(order.status);
        return {
            accessionNumber: order.accessionNumber,
            modality: order.modality,
            studyDescription: order.studyDescription,
            statusDescription: ClassToDisplayMapping.get(statusClass)?.toUpperCase(),
            statusDescriptionClass: statusClass,
            reportText: order.reportText,
            signTime: order.signedAt
        };
    }

    private isDecisionItem(scanRegion: ReferralScanRegion): boolean {
        return scanRegion.fromDecision;
    }

    private async initialiseSignalRUpdates(): Promise<void> {
        // We assume this will only contain one site in the imaging requests board.
        const selectedSite = this.orgSiteSelectionService.getSelectedSitesValue()[0];
        const selectedOrg = this.orgSiteSelectionService.getOrgSelectedValue().orgCode;
        await this.initReferralHubSubscription(selectedOrg, selectedSite);
        await this.initOrderHubSubscription(selectedOrg, selectedSite);
    }

    private async initReferralHubSubscription(orgCode: string, siteId: number): Promise<void> {
        this._hubSubscriptionId = uuidv4();

        try {
            await this.referralsService.subscribeToReferralUpdates(
                this._hubSubscriptionId,
                orgCode,
                [siteId],
                upsertNotification => this.handleUpsertedReferral(upsertNotification, orgCode, siteId),
                (entityId, entityType) => this.handleDeletedReferralEntity(entityId, entityType)
            );
        } catch (error) {
            console.warn(error);
        }
    }

    private async initOrderHubSubscription(orgCode: string, siteId: number): Promise<void> {
        this._hubConnection = await this.worklistService.startSignalRConnection(
            orgCode,
            [siteId],
            upsertedOrder => this.handleUpsertedOrder(upsertedOrder),
            accessionNumber => this.handleDeletedOrder(accessionNumber),
            accessionNumber => this.handleDeletedOrder(accessionNumber)
        );
    }

    private handleUpsertedReferral(upsertNotification: UpsertNotification, orgCode: string, siteId: number): void {
        if (upsertNotification.baseEntityType !== EntityType.Referral) {
            // we should only care about referrals
            return;
        }

        if (upsertNotification.orgCode !== orgCode || upsertNotification.siteId !== siteId) {
            // we only care about updates relevant to the current org code/site id
            console.warn(
                'Received unexpected upsert notification for org/site not equalling selected org/site',
                upsertNotification.orgCode,
                orgCode,
                siteId,
                upsertNotification.siteId
            );
            return;
        }

        const updatedReferral = upsertNotification.updatedEntityDetails as GetReferralDetails;

        if (this.isLinkedReferralUpdateForCurrentOrder(upsertNotification, updatedReferral)) {
            // handle updates to linked orders
            if (!this.imagingRequest.referralId) {
                // This block is entirely to handle linking a DTS Order to a referral
                // imaging request id is equal to order acession number for stand alone orders
                // so if this details modal is for a standalone order we need to recognise when it was linked
                const updatedReferral = upsertNotification.updatedEntityDetails as GetReferralDetails;
                this._pendingReferralUpdate$.next(updatedReferral);
            }

            const imagingRequestAccessions = this.getImagingRequestAccessions();
            updatedReferral.linkedOrders?.forEach(linkedOrder => {
                const orderAccession = linkedOrder.accessionNumber.toUpperCase();
                if (!imagingRequestAccessions.includes(orderAccession)) {
                    this._subscriptions.add(
                        this.worklistService
                            .get(orderAccession, WorklistItemDataSourceType.Order)
                            .pipe(catchError(error => CommonUtilities.getErrorMessage(error)))
                            .subscribe({
                                next: orderDetail => {
                                    const pendingOrderUpdates = this._pendingOrderUpdates$.value;
                                    pendingOrderUpdates.push(orderDetail);
                                    this._pendingOrderUpdates$.next(pendingOrderUpdates);
                                },
                                error: error => this.snackbarService.openSnackBar(error, SnackbarService.SNACK_BAR_CLASS_ALERT)
                            })
                    );
                }
            });
        } else if (updatedReferral.id === this.imagingRequest.referralId) {
            // something other than linked orders changed, update
            const currentPendingReferralUpdate = this._pendingReferralUpdate$.value;
            const isMoreRecent =
                !currentPendingReferralUpdate ||
                CommonUtilities.dateTimeIsEqualOrMoreRecent(updatedReferral.updatedAt, currentPendingReferralUpdate.updatedAt);

            if (isMoreRecent) {
                this._pendingReferralUpdate$.next(updatedReferral);
            }
        }
    }

    private isLinkedReferralUpdateForCurrentOrder(upsertNotification: UpsertNotification, updatedReferral: GetReferralDetails): boolean {
        if (upsertNotification.updatedEntityType !== EntityType.LinkedOrder) {
            return false;
        }

        return (
            updatedReferral.id == this.imagingRequest.referralId ||
            updatedReferral.linkedOrders.some(o => o.accessionNumber == this.imagingRequest.id)
        );
    }

    private handleDeletedReferralEntity(entityId: string, entityType: EntityType): void {
        switch (entityType) {
            case EntityType.Referral: {
                const referralId = Number.parseInt(entityId);
                if (referralId && referralId === this.imagingRequest?.referralId) {
                    this.snackbarService.openSnackBar(
                        'The referral for this imaging request was moved to a different site or deleted.',
                        SnackbarService.SNACK_BAR_CLASS_ALERT
                    );
                    this.closeForm();
                }
                break;
            }
            case EntityType.ReferralItem: {
                const referralItemId = this.hasReferralScanRegion(entityId, this.referralScanRequests, false);
                const deletedReferralItems = this._pendingDeletedReferralItem$.value;
                if (referralItemId && this.tryDeleteScanRegion(referralItemId, deletedReferralItems)) {
                    this._pendingDeletedReferralItem$.next(deletedReferralItems);
                }
                break;
            }
            case EntityType.DecisionItem: {
                const decisionItemId = this.hasReferralScanRegion(entityId, this.referralScanRequests, true);
                const deletedDecisionItems = this._pendingDeletedDecisionItems$.value;
                if (decisionItemId && this.tryDeleteScanRegion(decisionItemId, deletedDecisionItems)) {
                    this._pendingDeletedDecisionItems$.next(deletedDecisionItems);
                }
                break;
            }
            case EntityType.Prior: {
                const priorId = Number.parseInt(entityId);
                if (priorId) {
                    const matchingPrior = this.priors.find(prior => prior.id === priorId);
                    const deletedPriorIds = this._pendingDeletedPriors$.value;
                    if (matchingPrior && !deletedPriorIds.has(priorId)) {
                        deletedPriorIds.add(priorId);
                        this._pendingDeletedPriors$.next(deletedPriorIds);
                    }
                }
                break;
            }
            case EntityType.LinkedOrder: {
                const linkedOrder = this.hasLinkedOrder(entityId);
                if (linkedOrder) {
                    const deletedLinkedOrders = this._pendingDeletedLinkedOrderIds$.value;
                    if (this.tryDeleteLinkedOrder(linkedOrder.id, deletedLinkedOrders)) {
                        this._pendingDeletedLinkedOrderIds$.next(deletedLinkedOrders);
                    }
                }
                break;
            }
            default:
                // Ignore others.
                break;
        }
    }

    private hasLinkedOrder(entityId: string): GetLinkedOrder | null {
        const linkedOrderId = Number.parseInt(entityId);
        if (linkedOrderId && !Number.isNaN(linkedOrderId)) {
            const referralDetails = this.referralDetails$.getValue();
            const index = referralDetails?.linkedOrders?.findIndex(linkedOrder => linkedOrder.id === linkedOrderId);
            if (index != -1) {
                return referralDetails.linkedOrders[index];
            }
        }
        return null;
    }

    private tryDeleteLinkedOrder(itemId: number, deletedlinkedOrderIds: Set<number>): boolean {
        if (!deletedlinkedOrderIds.has(itemId)) {
            deletedlinkedOrderIds.add(itemId);
            return true;
        }
        return false;
    }

    private hasReferralScanRegion(entityId: string, scanRegions: ScanRegion[], isDecision: boolean): number | null {
        const itemId = Number.parseInt(entityId);
        if (itemId) {
            const referralScanRegions = scanRegions
                .filter(scanRegion => isReferralScanRegion(scanRegion))
                .map((scanRegion: ReferralScanRegion) => scanRegion);
            return referralScanRegions
                .filter(scanRegion => this.isDecisionItem(scanRegion) === isDecision)
                .find(scanRegion => scanRegion.id === itemId)
                ? itemId
                : null;
        }
        return null;
    }

    private tryDeleteScanRegion(itemId: number, deletedScanRegionIds: Set<number>): boolean {
        if (!deletedScanRegionIds.has(itemId)) {
            deletedScanRegionIds.add(itemId);
            return true;
        }
        return false;
    }

    private handleUpsertedOrder(order: IWorklistItemDto): void {
        const orderAccession = order.accessionNumber.toUpperCase();
        const imagingRequestAccessions = this.getImagingRequestAccessions();
        if (imagingRequestAccessions.includes(orderAccession)) {
            const pendingOrderUpdates = this._pendingOrderUpdates$.value;
            const pendingOrderUpdateAccessions = pendingOrderUpdates.map(order => order.accessionNumber.toUpperCase());
            if (pendingOrderUpdateAccessions.includes(orderAccession)) {
                const indexOfCurrentPendingOrder = pendingOrderUpdates.findIndex(order => order.accessionNumber === orderAccession);
                const currentPendingOrder = pendingOrderUpdates[indexOfCurrentPendingOrder];
                const isMoreRecent = DateTime.fromISO(order.lastUpdatedAt) >= DateTime.fromISO(currentPendingOrder.lastUpdatedAt);
                if (isMoreRecent) {
                    pendingOrderUpdates.splice(indexOfCurrentPendingOrder, 1, order);
                    this._pendingOrderUpdates$.next(pendingOrderUpdates);
                }
            } else {
                pendingOrderUpdates.push(order);
                this._pendingOrderUpdates$.next(pendingOrderUpdates);
            }
        }
    }

    private getImagingRequestAccessions(): string[] {
        return this.imagingRequest.scanRegions
            .filter(scanRegion => isOrderScanRegion(scanRegion))
            .map((scanRegion: OrderScanRegion) => scanRegion.accessionNumber.toUpperCase());
    }

    private handleDeletedOrder(accessionNumber: string): void {
        // Currently unhandled. We do not expect orders to be deleted once linked to a referral.
    }

    private async removeReferralHubSubscription(): Promise<void> {
        if (this._hubSubscriptionId) {
            await this.referralsService.unsubscribeFromReferralUpdates(this._hubSubscriptionId);
            this._hubSubscriptionId = null;
        }

        if (this._hubConnection) {
            await this._hubConnection.stop();
            this._hubConnection = null;
        }
    }

    closeForm(isSaved = false): void {
        this.dialogRef.close(isSaved);
    }

    ngOnDestroy(): void {
        this._subscriptions.unsubscribe();
        this.removeReferralHubSubscription();
    }
}

interface Prior {
    id: number;
    modality: string;
    bodyPart: string;
    date?: string;
}
